/* Billing-interval tabs of the plan sections (redesign-plans / redesign-plans-presale).

   Server-rendered cards are the source of truth, so both sections read correctly with no JS.
   A card finds its product by data-plan-key, never by a CSS class — two cards once shared a
   class and wrote into each other. A plan with no variant for the selected interval is
   hidden and the grid recomputes its columns; that is how annually-only Executive
   disappears from Monthly and Quarterly.

   Copy comes from data-* attributes only. Helpers are exported for the node tests; the
   custom elements are defined in a browser only. */

(() => {
  const CENTS_PER_UNIT = 100;
  const MONTHS_PER_YEAR = 12;
  const SINGLE_MONTH = 1;

  const normalizeTitle = (value) => String(value == null ? '' : value).trim().toLowerCase();

  const formatMoney = (cents) => {
    if (typeof window !== 'undefined' && window.theme?.Currency?.formatMoney && window.shopSettings?.moneyFormat) {
      return window.theme.Currency.formatMoney(Math.round(cents), window.shopSettings.moneyFormat);
    }
    return `$${(Math.round(cents) / CENTS_PER_UNIT).toFixed(2)}`;
  };

  // Mirrors Liquid's money_without_trailing_zeros: exact amount, a bare .00 dropped.
  const formatMoneyTrimmed = (cents) => formatMoney(cents).replace(/[.,]00(?=\D*$)/, '');

  // Whole units, for the effective per-month rate of a multi-month term only.
  const formatMoneyWhole = (cents) => formatMoneyTrimmed(Math.round(cents / CENTS_PER_UNIT) * CENTS_PER_UNIT);

  // Effective per-month rate, rounded to whole units for a clean headline.
  const perMonth = (cents, months) => Math.round(cents / months / CENTS_PER_UNIT) * CENTS_PER_UNIT;

  const fillTokens = (template, tokens) => Object.keys(tokens).reduce(
    (text, token) => text.split(`[${token}]`).join(tokens[token]),
    String(template == null ? '' : template)
  );

  const hasPrice = (variant) => !!variant && variant.price !== null && variant.price !== undefined;

  const findVariantByInterval = (product, interval) => {
    if (!product || !Array.isArray(product.variants)) return null;
    const wanted = normalizeTitle(interval);
    if (!wanted) return null;
    return product.variants.find((variant) => normalizeTitle(variant.interval) === wanted)
      // Older renders shipped no interval key; the title is the fallback.
      || product.variants.find((variant) => normalizeTitle(variant.title) === wanted)
      || null;
  };

  // A plan with no priced variant for an interval is not sold on that term.
  const visiblePlanKeys = (products, interval) => (Array.isArray(products) ? products : [])
    .filter((product) => hasPrice(findVariantByInterval(product, interval)))
    .map((product) => product.plan_key);

  const helpers = {
    normalizeTitle,
    formatMoney,
    formatMoneyTrimmed,
    formatMoneyWhole,
    perMonth,
    fillTokens,
    findVariantByInterval,
    visiblePlanKeys
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = helpers;

  if (typeof customElements === 'undefined' || typeof HTMLElement === 'undefined') return;
  if (customElements.get('membership-plan-tabs')) return;

  class MembershipPlanTabs extends HTMLElement {
    // Not the constructor: a custom element must not touch attributes or children before
    // it is connected, and the theme editor re-renders sections.
    connectedCallback() {
      this.grid = this.querySelector('[data-plan-grid]');
      this.cards = Array.from(this.querySelectorAll('[data-plan-card]'));
      this.tabs = Array.from(this.querySelectorAll('[data-plan-tab]'));
      this.products = this.readProducts();
      this.listeners = new AbortController();

      this.init();
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
    }

    readProducts() {
      const configElement = this.querySelector('[data-plan-config]');
      if (!configElement) return [];

      try {
        const parsed = JSON.parse(configElement.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.warn('membership-plan-tabs: unreadable plan config', error);
        return [];
      }
    }

    init() {
      if (!this.tabs.length || !this.products.length) return;

      const { signal } = this.listeners;

      this.tabs.forEach((tab) => {
        const isActive = tab.classList.contains('_active');
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        if (this.grid && this.grid.id) tab.setAttribute('aria-controls', this.grid.id);
        tab.addEventListener('click', () => this.selectTab(tab), { signal });
        tab.addEventListener('keydown', (event) => this.handleKeydown(event, tab), { signal });
      });

      const tabsList = this.tabs[0].closest('[data-plan-tabs-list]');
      if (tabsList) tabsList.setAttribute('role', 'tablist');

      // An interval no plan sells would leave every card hidden, i.e. an empty section. Hide
      // the tab instead, and start on one that has something to show.
      this.tabs.forEach((tab) => {
        const sells = visiblePlanKeys(this.products, tab.dataset.interval).length > 0;
        tab.classList.toggle('_hidden', !sells);
      });

      // A hidden tab keeps no state a screen reader could land on.
      this.tabs.filter((tab) => tab.classList.contains('_hidden')).forEach((tab) => {
        tab.classList.remove('_active');
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
      });

      // Nothing on offer, or a single term: the switcher itself has nothing to switch.
      const sellingTabs = this.tabs.filter((tab) => !tab.classList.contains('_hidden'));
      if (sellingTabs.length <= 1 && tabsList) tabsList.classList.add('_hidden');
      if (!sellingTabs.length) return;

      this.tabs = sellingTabs;

      const activeTab = sellingTabs.find((tab) => tab.classList.contains('_active')) || sellingTabs[0];
      this.selectTab(activeTab);
    }

    handleKeydown(event, tab) {
      // Resolved now, not when the listener was bound: hidden intervals are dropped from
      // this.tabs after binding, so a captured index would point past the end.
      const index = this.tabs.indexOf(tab);
      if (index < 0) return;

      const lastIndex = this.tabs.length - 1;
      let targetIndex = null;

      if (event.key === 'ArrowRight') targetIndex = index === lastIndex ? 0 : index + 1;
      if (event.key === 'ArrowLeft') targetIndex = index === 0 ? lastIndex : index - 1;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = lastIndex;

      if (targetIndex === null) return;

      event.preventDefault();
      const targetTab = this.tabs[targetIndex];
      this.selectTab(targetTab);
      targetTab.focus();
    }

    selectTab(tab) {
      this.tabs.forEach((candidate) => {
        const isActive = candidate === tab;
        candidate.classList.toggle('_active', isActive);
        candidate.setAttribute('aria-selected', isActive ? 'true' : 'false');
        candidate.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      if (this.grid && tab.id) this.grid.setAttribute('aria-labelledby', tab.id);

      this.applyTab(tab);
    }

    applyTab(tab) {
      const context = {
        interval: normalizeTitle(tab.dataset.interval),
        period: tab.dataset.period || '',
        months: parseInt(tab.dataset.months, 10) || SINGLE_MONTH,
        perk: (tab.dataset.perk || '').trim(),
        cadence: tab.dataset.cadence || ''
      };

      const visible = visiblePlanKeys(this.products, context.interval);
      let visibleCards = 0;

      this.cards.forEach((card) => {
        if (!visible.includes(card.dataset.planKey)) {
          card.classList.add('_hidden');
          return;
        }

        const product = this.products.find((candidate) => candidate.plan_key === card.dataset.planKey);
        const variant = findVariantByInterval(product, context.interval);

        card.classList.remove('_hidden');
        visibleCards += 1;

        try {
          this.renderCard(card, product, variant, context);
        } catch (error) {
          // One broken card must not take the whole section down.
          console.warn('membership-plan-tabs: could not render card', card.dataset.planKey, error);
        }
      });

      if (this.grid) this.grid.style.setProperty('--plan-cards-columns', visibleCards || 1);
    }

    // Every CTA, not the first: a card renders one for mobile and one for desktop.
    updateCta(card, variant) {
      if (!variant.url) return;
      card.querySelectorAll('[data-plan-cta]').forEach((cta) => {
        if (cta.tagName === 'A') cta.setAttribute('href', variant.url);
      });
    }

    renderCard(card, product, variant, context) {
      card.querySelectorAll('[data-plan-price]').forEach((element) => {
        element.textContent = `${formatMoneyTrimmed(variant.price)}/`;
      });

      card.querySelectorAll('[data-plan-period]').forEach((element) => {
        element.textContent = context.period;
      });

      const hasCompareAt = variant.compare_at_price && variant.compare_at_price > variant.price;
      card.querySelectorAll('[data-plan-compare]').forEach((element) => {
        element.textContent = hasCompareAt ? formatMoneyTrimmed(variant.compare_at_price) : '';
      });

      this.updateCta(card, variant);
    }
  }

  /* Pre-sale cards headline the per-month rate of the term, with the founding discount run
     through the same math. State is a state class, never a BEM modifier: _hidden removes the
     element, _invisible keeps its line reserved so the card cannot jump between tabs. */
  const PRESALE_HIDDEN_CLASS = {
    rateOld: '_hidden',
    ribbon: '_hidden',
    rateLabel: '_invisible',
    perk: '_invisible',
    seal: '_hidden'
  };

  class MembershipPresalePlanTabs extends MembershipPlanTabs {
    renderCard(card, product, variant, context) {
      // A single-month term is the amount actually charged, so it is printed exactly as the
      // server printed it. Only a multi-month term shows a derived, rounded per-month rate.
      const isSingleTerm = context.months <= SINGLE_MONTH;
      const rateOf = (cents) => (isSingleTerm ? cents : perMonth(cents, context.months));
      const printRate = (cents) => (isSingleTerm ? formatMoneyTrimmed(cents) : formatMoneyWhole(cents));

      const effective = rateOf(variant.price);
      const hasCompareAt = variant.compare_at_price && variant.compare_at_price > variant.price;
      const reference = hasCompareAt ? rateOf(variant.compare_at_price) : null;

      card.querySelectorAll('[data-rate]').forEach((element) => {
        element.textContent = printRate(effective);
      });

      const showReference = reference !== null && reference > effective;
      card.querySelectorAll('[data-rate-old]').forEach((element) => {
        element.textContent = showReference ? printRate(reference) : '';
        element.classList.toggle(PRESALE_HIDDEN_CLASS.rateOld, !showReference);
      });

      card.querySelectorAll('[data-founders-seal]').forEach((element) => {
        element.classList.toggle(PRESALE_HIDDEN_CLASS.seal, !hasCompareAt);
      });

      const ribbon = card.querySelector('[data-save-ribbon]');
      if (ribbon) {
        ribbon.classList.toggle(PRESALE_HIDDEN_CLASS.ribbon, !hasCompareAt);
        if (hasCompareAt) {
          const annualSavings = (variant.compare_at_price - variant.price) * (MONTHS_PER_YEAR / context.months);
          const savingsValue = ribbon.querySelector('[data-founder-save]');
          if (savingsValue) savingsValue.textContent = formatMoneyWhole(annualSavings);
        }
      }

      card.querySelectorAll('[data-rate-label]').forEach((element) => {
        element.classList.toggle(PRESALE_HIDDEN_CLASS.rateLabel, context.months <= SINGLE_MONTH);
      });

      const billed = card.querySelector('[data-billed]');
      if (billed) {
        billed.textContent = context.months <= SINGLE_MONTH
          ? billed.dataset.billedMonthlyText || ''
          : fillTokens(billed.dataset.billedTermText, {
            amount: formatMoney(variant.price),
            cadence: context.cadence
          });
      }

      const perkChip = card.querySelector('[data-perk-chip]');
      if (perkChip) {
        perkChip.classList.toggle(PRESALE_HIDDEN_CLASS.perk, context.perk === '');
        const perkText = perkChip.querySelector('[data-perk]');
        if (perkText) perkText.textContent = context.perk;
      }

      this.updateCta(card, variant);
    }
  }

  customElements.define('membership-plan-tabs', MembershipPlanTabs);
  customElements.define('membership-presale-plan-tabs', MembershipPresalePlanTabs);
})();
